import clsx from 'clsx';
import Box from '@mui/system/Box';
import Grid from '@mui/material/Grid';
import styled from '@mui/styles/styled';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ZoneRowComponent from './ZoneRowComponent';
import { addEmptyZone } from '../store/zonesSlice';
import { MoreOptionDialog } from '@blackhyve/common';
import { useDispatch, useSelector } from 'react-redux';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Fragment, memo, useContext, useState } from 'react';
import ColorDropdown from '../../../components/ColorDropdown';
import DragIndicator from '@mui/icons-material/DragIndicator';
import CopyIconComponent from '@blackhyve/common/src/icons/Copy';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIconComponent from '@blackhyve/common/src/icons/Delete';
import InlineEditCardTitle from 'components/common/v3/InlineEditCardTitle';
import {
  createLocation,
  deleteLocation,
  duplicateLocation,
  removeEmptyLocation,
  selectLocationById,
  updateLocation,
} from '../store/locationsSlice';
import DisplayWithEditAccess, { EditContext } from 'components/common/v3/DisplayWithEditAccess';

const AddNewZoneButton = styled('span')(({ theme }) => ({
  fontSize: '13px',
  cursor: 'pointer',
  textTransform: 'uppercase',
  borderRadius: '2px',
  padding: '5px',
  color: 'grey',
  '&:hover': {
    color: 'white',
    backgroundColor: theme.palette.primary.main,
  },
}));

const LocationRowComponent = ({ locationIndex, id }) => {
  const dispatch = useDispatch();
  const canEdit = useContext(EditContext);
  const [collapse, setCollapse] = useState(true);
  const [isDraggable, setIsDraggable] = useState(canEdit);
  const zones = useSelector((state) => selectLocationById(state, id)?.zones);

  const handleSetDraggable = (value) => {
    setIsDraggable(value);
  };

  const handleNewZone = () => {
    dispatch(addEmptyZone({ locationId: id, id: Date.now() * -1 }));
  };

  return (
    <Draggable
      draggableId={`location-${id}`}
      index={locationIndex}
      isDragDisabled={!isDraggable}
      key={`${id}-zone`}
    >
      {(provided, snapshot) => (
        <Grid
          container
          item
          ref={provided.innerRef}
          {...provided.draggableProps}
          bgcolor={'white'}
          style={{ ...provided.draggableProps.style }}
          sx={{
            border: '.5px solid',
            borderColor: 'grey.400',
            borderRadius: '2px',
            padding: (theme) => theme.spacing(3),
            mb: (theme) => theme.spacing(1),
          }}
        >
          <HeadingComponent
            collapse={collapse}
            dragHandleProps={provided.dragHandleProps}
            id={id}
            setCollapse={setCollapse}
          />
          <Droppable droppableId={'location-' + id} type={`ZONE-` + id}>
            {(provided) => (
              <Grid container {...provided.droppableProps} ref={provided.innerRef}>
                {collapse &&
                  zones &&
                  zones.length > 0 &&
                  zones.map((zoneId, index) => {
                    return (
                      <ZoneRowComponent
                        handleSetDraggable={handleSetDraggable}
                        id={zoneId}
                        index={index}
                        isDraggable={isDraggable}
                        key={`zone-${zoneId}`}
                      />
                    );
                  })}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
          <DisplayWithEditAccess>
            <Box mt={1}>
              <AddNewZoneButton onClick={handleNewZone}>+ New zone</AddNewZoneButton>
            </Box>
          </DisplayWithEditAccess>
        </Grid>
      )}
    </Draggable>
  );
};

const HeadingComponent = (props) => {
  const dispatch = useDispatch();
  const canEdit = useContext(EditContext);

  const { id, dragHandleProps, setCollapse, collapse } = props;
  const [loading, setLoading] = useState(false);
  const name = useSelector((state) => selectLocationById(state, id)?.name);
  const color = useSelector((state) => selectLocationById(state, id)?.color);
  const createdAt = useSelector((state) => selectLocationById(state, id)?.created_at);
  const projectId = useSelector((state) => selectLocationById(state, id)?.project_id);

  const [openMoreOptionDialog, setOpenMoreOptionDialog] = useState(null);

  const handleRename = (requestParameters) => {
    setLoading(true);
    if (id < 0) {
      dispatch(createLocation(requestParameters)).then(() => setLoading(false));
    } else {
      dispatch(updateLocation(requestParameters)).then(() => setLoading(false));
    }
  };

  const handleDuplicate = () => {
    setLoading(true);
    dispatch(duplicateLocation({ id: id })).then(() => setLoading(false));
  };

  const handleDelete = () => {
    setLoading(true);
    if (createdAt) {
      dispatch(deleteLocation({ id })).then(() => setLoading(false));
    } else {
      dispatch(removeEmptyLocation({ id }));
      setLoading(false);
    }
  };

  const handleOpenMoreOptionDialog = (event) => {
    setOpenMoreOptionDialog(event.target);
  };

  const handleCloseMoreOptionDialog = () => {
    setOpenMoreOptionDialog(null);
  };

  const handleSaveColor = (newColor) => {
    setLoading(true);
    dispatch(
      updateLocation({
        id: id,
        color: newColor,
      })
    ).then(() => setLoading(false));
  };

  const optionMenu = [
    {
      name: 'Duplicate Location',
      icon: <CopyIconComponent />,
      action: handleDuplicate,
    },
    {
      name: 'Delete location',
      icon: <DeleteIconComponent color="red" />,
      action: handleDelete,
      style: { color: 'red' },
    },
  ];

  const requestParameters = {
    id: id,
    project_id: projectId,
  };

  const inlineEditStyle = {
    textAlign: 'initial',
    fontWeight: '600',
    fontSize: '18px',
    lineHeight: '21px',
    color: '#000000',
    marginBottom: '0px !important',
  };

  return (
    <Grid container item>
      <Grid container item alignItems={'center'} xs={12}>
        {loading ? (
          <Skeleton className="skeleton-row" variant="text" />
        ) : (
          <Fragment>
            <Grid item {...dragHandleProps}>
              <DragIndicator sx={{ color: 'grey.600' }} />
            </Grid>
            <Grid item>
              <IconButton
                disableFocusRipple={true}
                disableRipple={true}
                size="medium"
                sx={{ padding: 0 }}
                onClick={() => setCollapse(!collapse)}
              >
                {collapse ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
              </IconButton>
            </Grid>
            <Grid item>
              <InlineEditCardTitle
                fieldName={'name'}
                handleUpdate={handleRename}
                isDefaultEdit={id < 0}
                isEditable={canEdit}
                locationLabelStyle={inlineEditStyle}
                requestParameters={requestParameters}
                textToDisplay={name}
              />
            </Grid>
            <Grid
              item
              style={{
                display: 'flex',
                marginLeft: '10px',
                marginRight: '10px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ColorDropdown color={color} disabled={!canEdit} handleSaveColor={handleSaveColor} />
            </Grid>
            <DisplayWithEditAccess>
              <IconButton
                disableFocusRipple={true}
                disableRipple={true}
                size="small"
                style={{ padding: 0, marginLeft: 2 }}
                onClick={handleOpenMoreOptionDialog}
              >
                <MoreHorizIcon className={clsx('moreHoriz', 'show-icon')} fontSize="small" />
              </IconButton>
              {openMoreOptionDialog && (
                <MoreOptionDialog
                  anchorEl={openMoreOptionDialog}
                  handleClose={handleCloseMoreOptionDialog}
                  id="more-option-popover"
                  open={Boolean(openMoreOptionDialog)}
                  optionMenu={optionMenu}
                />
              )}
            </DisplayWithEditAccess>
          </Fragment>
        )}
      </Grid>
      <Grid container item ml={'auto'} xs={5}>
        <Grid item xs={2}>
          <Typography display="block" textAlign="center" variant="overline">
            Color
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography display="block" textAlign="center" variant="overline">
            Priority
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography display="block" textAlign="center" variant="overline">
            Percent
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography display="block" textAlign="center" variant="overline">
            Sqft
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography display="block" textAlign="center" variant="overline">
            Density
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

const MemorizedLocation = memo(LocationRowComponent);

export default MemorizedLocation;
